import React, { useState, useEffect } from 'react';
import { selectMicIsMuted } from 'flow/selectors/mute-selectors';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import Icon from '../ui-components/Icon';
import MenuItem from '../ui-components/MenuItem';
import { DisplayMode } from 'flow/utils/rtc-utils';
import useAppContext from 'flow/hooks/useAppContext';
import IconButton from '../ui-components/IconButton';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { getHoverTextShortcutMic } from 'flow/utils/call-utils';
import {
    mutePhone,
    sendDTMF,
    toggleFocusedCall,
} from 'flow/actions/phone-actions';
import {
    updateLayoutColumns,
    setPreferredCallCanvasSize,
} from 'flow/actions/app-actions';
import {
    isCoListening,
    allowToggleCall,
    getNumLocalCalls,
    isLocalFocusedCallSessionEstablished,
} from 'flow/selectors/call-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { generatePath } from 'flow/utils/router-utils';
import { twMerge } from 'tailwind-merge';
import { useHistory } from 'react-router-dom';
import { routes } from 'flow/types/routes';
import { setActiveMeetingViewAction } from 'flow/actions/meeting-actions';
import ContextMenu from '../ContextMenu';
import DtmfDialpad from './DtmfDialpad';

interface Props {
    onNewCall: () => void;
}

const BottomBarFooter = ({ onNewCall }: Props) => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { i18next } = useAppContext();

    const [anchorEl, setAnchorEl] = useState(null);
    const [presentDtmf, setPresentDtmf] = useState(false);
    const [dialpadTypedNumber, setDialpadTypedNumber] = useState('');

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const isMuted = useAppSelector(selectMicIsMuted);
    const numLocalCalls = useAppSelector(getNumLocalCalls);
    const isCoListeningSession = useAppSelector(isCoListening);
    const canToggleCalls = useAppSelector(allowToggleCall);
    const canSendDTMF =
        useAppSelector(isLocalFocusedCallSessionEstablished) &&
        !isCoListeningSession;
    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);

    const canMuteUnmute = numLocalCalls > 0 && !isCoListeningSession;

    const onKeyDown = (event: KeyboardEvent) => {
        const superKey = event.ctrlKey || event.metaKey;
        if ((event.key === 'D' || event.key === 'd') && superKey) {
            event.preventDefault();

            if (canMuteUnmute) {
                addUserPhoneLogEntry(
                    `Comm canvas -> ${isMuted ? 'unmute' : 'mute'} clicked`,
                );
            }
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    });

    return (
        <div
            className={twMerge(
                'flex justify-end gap-x-1',
                isSinglePaneMode ? '-order-1 w-full' : '',
            )}
        >
            <IconButton
                aria-label={i18next.t('Call.Canvas.Title.Minimize')}
                // text-white because the icon inside now follows currentColor,
                // and this bar sets no colour of its own over its black ground.
                className={twMerge(
                    'text-white hover:bg-gray-600',
                    isSinglePaneMode ? 'size-6' : 'size-8',
                )}
                onClick={() => {
                    addUserPhoneLogEntry(
                        'Comm canvas -> set thumbnail mode clicked',
                    );
                    dispatch(
                        setPreferredCallCanvasSize(DisplayMode.THUMBNAIL.key),
                    );
                    dispatch(
                        setActiveMeetingViewAction(
                            isSinglePaneMode
                                ? DisplayMode.FULLSCREEN.key
                                : DisplayMode.THUMBNAIL.key,
                        ),
                    );
                    dispatch(updateLayoutColumns());
                }}
            >
                <Icon iconKey="right_panel_inverted" />
            </IconButton>

            <div aria-controls="dtmf-container" aria-haspopup="true" />

            <ContextMenu
                triggerLabel={i18next.t('menu.more_options')}
                setMenuButtonAnchor={setAnchorEl}
                placement="top-start"
                className={twMerge(
                    'flex items-center justify-center bg-transparent text-white hover:bg-gray-600',
                    isSinglePaneMode ? 'size-6' : 'size-8',
                )}
            >
                <MenuItem
                    onClick={() => {
                        addUserPhoneLogEntry('Comm canvas -> settings clicked');
                        history.push(
                            generatePath(routes['settings.softphone'], {
                                extensionKey: ownExtensionKey,
                            }),
                        );
                    }}
                    className="h-8"
                >
                    <Icon iconKey="general" className="m-1" />
                    {i18next.t('settings.softphone.telephony_and_audio')}
                </MenuItem>
                {canToggleCalls && (
                    <MenuItem
                        onClick={() => {
                            addUserPhoneLogEntry(
                                'Comm canvas -> toggle call clicked',
                            );
                            dispatch(toggleFocusedCall);
                        }}
                        className="h-8"
                    >
                        <Icon iconKey="toggle_call" className="m-1" />
                        {i18next.t('Call.Canvas.Title.Toggle')}
                    </MenuItem>
                )}
                {canSendDTMF && (
                    <MenuItem
                        onClick={() => {
                            setPresentDtmf(
                                (prevPresentDtmf) => !prevPresentDtmf,
                            );
                        }}
                        className="h-8"
                    >
                        <Icon iconKey="dialpad" className="m-1" />
                        {i18next.t('Tooltip.Title.DialPad')}
                    </MenuItem>
                )}

                {onNewCall && (
                    <MenuItem
                        onClick={() => {
                            onNewCall();
                        }}
                        className="h-8"
                    >
                        <Icon iconKey="call" className="m-1" />
                        {i18next.t('call.new')}
                    </MenuItem>
                )}
                {canMuteUnmute ? (
                    <MenuItem
                        onClick={() => {
                            addUserPhoneLogEntry(
                                `Comm canvas -> ${
                                    isMuted ? 'unmute' : 'mute'
                                } clicked`,
                            );
                            dispatch(mutePhone(!isMuted));
                        }}
                        className="h-8"
                    >
                        <Icon iconKey="mic" className="m-1" />
                        {isMuted
                            ? i18next.t('Call.Canvas.Title.Unmute')
                            : i18next.t('Call.Canvas.Title.Mute')}
                        {getHoverTextShortcutMic()}
                    </MenuItem>
                ) : null}
            </ContextMenu>

            {presentDtmf && (
                <DtmfDialpad
                    handleSendDtmf={(symbol: string) => {
                        addUserPhoneLogEntry(
                            'Comm canvas -> DTMF symbol clicked',
                        );
                        dispatch(sendDTMF(symbol));
                    }}
                    number={dialpadTypedNumber}
                    setNumber={setDialpadTypedNumber}
                    handleClose={() => {
                        setPresentDtmf(false);
                        setAnchorEl(null);
                    }}
                    anchorEl={anchorEl}
                />
            )}
        </div>
    );
};

export default React.memo(BottomBarFooter);
